import { z } from "zod";

import { ticketSchema } from "./ticket";

const textractDataSchema = z.object({
  layout: z.any(),
  extra_layout_info: z.any(),
  raw: z.any(),
});

export type TextractData = z.infer<typeof textractDataSchema>;

// Spreadsheet cell record stored within an aicache doc for a single sheet.
// `formula` is null for cells that hold a literal value; both `formula` and
// `value` are present when the cell has a formula whose computed value Excel
// had previously cached.
export const spreadsheetCellSchema = z.object({
  ref: z.string(), // A1 notation, e.g. "D5"
  value: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
  formula: z.string().nullable().optional(),
  dtype: z
    .enum(["string", "number", "boolean", "date", "error", "null"])
    .optional(),
});

export type SpreadsheetCell = z.infer<typeof spreadsheetCellSchema>;

export const spreadsheetChartSchema = z.object({
  title: z.string().optional(),
  type: z.string().optional(),
  anchorRange: z.string().optional(),
});

export const spreadsheetImageSchema = z.object({
  anchorCell: z.string().optional(),
  name: z.string().optional(),
});

export const spreadsheetConditionalFormatSchema = z.object({
  range: z.string(),
  rule: z.string().optional(),
});

export const aiCacheSchema = z.object({
  key: z.string(), // original image key, could be tile piece image
  parentKey: z.string().optional(), // original file, could be PDF or larger image that is now tiled
  parentAssetId: z.string().optional(),
  convertedKey: z.string().optional(), // AI resized image
  textract: textractDataSchema.optional(),
  ticket: z.union([z.string(), ticketSchema.partial()]),
  page: z.coerce.number().optional(),
  x: z.coerce.number().optional(), // original position X
  y: z.coerce.number().optional(), // original position Y
  timestamp: z.coerce.number().optional(), // Video timestamp integer position
  dimensions: z.array(z.coerce.number()).optional(), // resized AI image dimensions
  parentDimensions: z.array(z.coerce.number()).optional(), // parent original image dimensions
  originalDimensions: z.array(z.coerce.number()).optional(), // image used that could be tiled, without resize
  boardId: z.string().optional(),
  version: z.coerce.number().optional(), // Layout version used, internal versioning from Samba
  legal_claims: z
    .object({
      claims: z.array(z.string()).optional(),
      disclaimers: z.array(z.string()).optional(),
      warnings: z.array(z.string()).optional(),
    })
    .optional(),

  // Document-shape discriminator: "text" (markdown extraction) | "spreadsheet" |
  // "visual" (page render) | undefined (legacy image tile).
  fileType: z.string().optional(),
  markdownContent: z.string().optional(),

  // Spreadsheet-only fields. One aicache doc per sheet.
  sheetIndex: z.coerce.number().int().nonnegative().optional(),
  sheetName: z.string().optional(),
  headers: z.array(z.string()).optional(),
  cells: z.array(spreadsheetCellSchema).optional(),
  mergedRanges: z.array(z.string()).optional(),
  namedRanges: z.record(z.string()).optional(),
  hasVisualElements: z.boolean().optional(),
  charts: z.array(spreadsheetChartSchema).optional(),
  images: z.array(spreadsheetImageSchema).optional(),
  conditionalFormats: z.array(spreadsheetConditionalFormatSchema).optional(),
  truncated: z.boolean().optional(),
  extractionFailed: z.string().optional(),
});

export type AICache = z.infer<typeof aiCacheSchema>;
